import {
  Activity,
  AlertTriangle,
  Apple,
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Cloud,
  CloudOff,
  Dumbbell,
  Download,
  Droplets,
  Flame,
  HeartPulse,
  Home,
  Info,
  Leaf,
  LogOut,
  Menu,
  MoonStar,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Trash2,
  TrendingDown,
  Trophy,
  Utensils,
  Weight,
  WifiOff,
  X,
} from "lucide-react";
import { FormEvent, ReactNode, useCallback, useEffect, useState } from "react";
import {
  ApiError,
  apiDownload,
  apiGet,
  apiMutation,
  flushOfflineQueue,
  setCsrfToken,
} from "./lib/api";
import {
  clearOfflineData,
  listConflicts,
  removeConflict,
  retryConflict,
  type OfflineConflict,
} from "./lib/offline";
import {
  authorizeHealthKit,
  collectHealthKit,
  healthKitAvailability,
} from "./lib/healthkit";
import {
  beginNativeGoogleSignIn,
  clearNativeTokens,
  isNativeApp,
  restoreNativeTokens,
  saveNativeTokens,
} from "./lib/nativeAuth";
import {
  foodMacros,
  formatDuration,
  localDate,
  percent,
  workoutVolume,
} from "./lib/calculations";
import type {
  AuthState,
  DashboardData,
  Food,
  FrequentMeal,
  GoalType,
  MealDay,
  MealEntry,
  MealType,
  HydrationLog,
  Measurement,
  Profile,
  User,
  WorkoutSession,
  WorkoutSet,
  WorkoutTemplate,
} from "./types";

type Tab = "today" | "food" | "training" | "trends" | "settings";
type Toast = {
  id: string;
  tone: "success" | "info" | "error";
  message: string;
};

const today = () => localDate(new Date());
const goalLabels: Record<GoalType, string> = {
  fat_loss: "減脂",
  muscle_gain: "增肌",
  maintain: "維持",
};
const mealLabels: Record<MealType, string> = {
  breakfast: "早餐",
  lunch: "午餐",
  dinner: "晚餐",
  snack: "點心",
};

function App() {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    csrfToken: null,
    ownerExists: true,
    googleSsoConfigured: false,
  });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("today");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [conflicts, setConflicts] = useState<OfflineConflict[]>([]);

  const notify = useCallback(
    (message: string, tone: Toast["tone"] = "success") => {
      const id = crypto.randomUUID();
      setToasts((items) => [...items, { id, message, tone }]);
      window.setTimeout(
        () => setToasts((items) => items.filter((item) => item.id !== id)),
        3400,
      );
    },
    [],
  );

  const refreshAuth = useCallback(async () => {
    setLoading(true);
    try {
      await restoreNativeTokens();
      const status = await apiGet<{
        ownerExists: boolean;
        googleSsoConfigured: boolean;
      }>("/api/v1/status");
      if (!status.ownerExists) {
        setAuth({
          user: null,
          csrfToken: null,
          ownerExists: false,
          googleSsoConfigured: status.googleSsoConfigured,
        });
        return;
      }
      try {
        const me = await apiGet<{ user: User; csrfToken: string }>(
          "/api/v1/auth/me",
        );
        setCsrfToken(me.csrfToken);
        setAuth({
          user: me.user,
          csrfToken: me.csrfToken,
          ownerExists: true,
          googleSsoConfigured: status.googleSsoConfigured,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setAuth({
            user: null,
            csrfToken: null,
            ownerExists: true,
            googleSsoConfigured: status.googleSsoConfigured,
          });
        } else throw error;
      }
    } catch {
      notify("暫時無法連上雲端，請稍後再試。", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);
  useEffect(() => {
    const online = async () => {
      setIsOnline(true);
      const result = await flushOfflineQueue();
      if (result.sent) notify(`已同步 ${result.sent} 筆離線紀錄`);
      if (result.conflicts.length) {
        setConflicts(await listConflicts());
        notify(`${result.conflicts.length} 筆紀錄需要確認`, "info");
      }
    };
    const offline = () => setIsOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [notify]);

  useEffect(() => {
    if (!auth.user) return;
    void listConflicts().then(setConflicts);
  }, [auth.user]);

  useEffect(() => {
    const user = auth.user;
    if (
      !user?.onboardingCompleted ||
      !isOnline ||
      localStorage.getItem("hengri:healthkit-enabled") !== "true"
    )
      return;
    const runKey = `hengri:healthkit-auto:${user.id}`;
    if (sessionStorage.getItem(runKey)) return;
    sessionStorage.setItem(runKey, "true");
    void (async () => {
      try {
        if (!(await healthKitAvailability())) return;
        const batch = await collectHealthKit(7, user.timezone);
        if (!batch.daily.length && !batch.workouts.length) return;
        const result = await apiMutation<{ accepted: number }>(
          "/api/v1/health/import",
          "POST",
          batch,
        );
        if (!("queued" in result) && result.accepted > 0)
          notify("Apple Health 已自動更新");
      } catch {
        // Permission may have been revoked. Wait for a manual sync without blocking the app.
      }
    })();
  }, [auth.user, isOnline, notify]);

  const onAuthenticated = (user: User, csrf: string | null) => {
    setCsrfToken(csrf);
    setAuth((state) => ({
      ...state,
      user,
      csrfToken: csrf,
      ownerExists: true,
    }));
  };

  const logout = async () => {
    try {
      await apiMutation("/api/v1/auth/logout", "POST");
    } catch {
      /* clear locally regardless */
    }
    await clearOfflineData();
    await clearNativeTokens();
    setCsrfToken(null);
    setAuth((state) => ({
      ...state,
      user: null,
      csrfToken: null,
      ownerExists: true,
    }));
    setTab("today");
  };

  if (loading) return <LoadingScreen />;
  if (!auth.user)
    return (
      <AuthScreen
        ownerExists={auth.ownerExists}
        googleSsoConfigured={auth.googleSsoConfigured}
        onAuthenticated={onAuthenticated}
      />
    );
  if (!auth.user.onboardingCompleted) {
    return (
      <Onboarding
        user={auth.user}
        onComplete={(user) => setAuth((state) => ({ ...state, user }))}
        notify={notify}
      />
    );
  }

  return (
    <div className="app-shell">
      <SideRail active={tab} onChange={setTab} user={auth.user} />
      <div className="app-content">
        <TopBar
          user={auth.user}
          online={isOnline}
          tab={tab}
          onLogout={logout}
        />
        <main id="main-content" tabIndex={-1}>
          {tab === "today" && <TodayPage notify={notify} navigate={setTab} />}
          {tab === "food" && <FoodPage notify={notify} />}
          {tab === "training" && <TrainingPage notify={notify} />}
          {tab === "trends" && <TrendsPage notify={notify} />}
          {tab === "settings" && (
            <SettingsPage
              user={auth.user}
              notify={notify}
              onLogout={logout}
              onUserChange={(user) => setAuth((state) => ({ ...state, user }))}
            />
          )}
        </main>
      </div>
      <BottomNav active={tab} onChange={setTab} />
      {conflicts.length > 0 && (
        <ConflictCenter
          conflicts={conflicts}
          onKeepCloud={async (id) => {
            await removeConflict(id);
            setConflicts(await listConflicts());
            notify("已保留雲端版本");
          }}
          onRetry={async (id) => {
            await retryConflict(id);
            const result = await flushOfflineQueue();
            setConflicts(await listConflicts());
            notify(result.sent ? "已套用離線變更" : "已排入重新同步", "info");
          }}
        />
      )}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.tone}`}>
            {toast.tone === "success" ? <Check /> : <Info />}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <Brand />
      <div className="loading-dots">
        <i />
        <i />
        <i />
      </div>
      <p>整理今天的進度…</p>
    </div>
  );
}

function ConflictCenter({
  conflicts,
  onKeepCloud,
  onRetry,
}: {
  conflicts: OfflineConflict[];
  onKeepCloud: (id: string) => Promise<void>;
  onRetry: (id: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="conflict-fab" onClick={() => setOpen(true)}>
        <AlertTriangle /> {conflicts.length} 筆待確認
      </button>
      {open && (
        <Modal
          title="離線資料需要確認"
          subtitle="同一筆資料已在其他裝置更新，請選擇要保留的版本。"
          onClose={() => setOpen(false)}
        >
          <div className="conflict-list">
            {conflicts.map((conflict) => (
              <article className="conflict-item" key={conflict.id}>
                <span className="metric-icon gold"><AlertTriangle /></span>
                <div>
                  <strong>{conflictLabel(conflict.mutation.path)}</strong>
                  <p>{conflict.message}</p>
                  <small>{new Date(conflict.detectedAt).toLocaleString("zh-TW")}</small>
                </div>
                <div className="conflict-actions">
                  <button className="secondary-button small" onClick={() => void onKeepCloud(conflict.id)}>
                    保留雲端
                  </button>
                  <button className="primary-button small" onClick={() => void onRetry(conflict.id)}>
                    套用我的變更
                  </button>
                </div>
              </article>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function conflictLabel(path: string): string {
  if (path.includes("workout-templates")) return "訓練模板";
  if (path.includes("measurements")) return "身體測量";
  if (path.includes("hydration")) return "飲水紀錄";
  if (path.includes("meals")) return "飲食紀錄";
  if (path.includes("profile")) return "個人設定";
  return "離線變更";
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? "compact" : ""}`}>
      <img src="/brand-mark.svg" alt="" />
      <div>
        <strong>衡日</strong>
        {!compact && <span>每天多一點平衡</span>}
      </div>
    </div>
  );
}

function AuthScreen({
  ownerExists,
  googleSsoConfigured,
  onAuthenticated,
}: {
  ownerExists: boolean;
  googleSsoConfigured: boolean;
  onAuthenticated: (user: User, csrf: string | null) => void;
}) {
  const [mode, setMode] = useState<"login" | "setup">(
    ownerExists ? "login" : "setup",
  );
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("auth_error");
    if (!code) return;
    const messages: Record<string, string> = {
      GOOGLE_ACCESS_DENIED: "Google 登入已取消。",
      GOOGLE_ACCOUNT_NOT_ALLOWED: "請使用 owner 帳號的 Google 電子郵件登入。",
      GOOGLE_ACCOUNT_ALREADY_LINKED: "此 owner 已綁定其他 Google 帳號。",
      GOOGLE_EMAIL_NOT_VERIFIED: "Google 電子郵件尚未通過驗證。",
      INVALID_OAUTH_STATE: "登入驗證已過期，請重新嘗試。",
      GOOGLE_SSO_NOT_CONFIGURED: "Google 登入尚未完成設定。",
    };
    setError(messages[code] ?? "Google 登入失敗，請重新嘗試。");
    params.delete("auth_error");
    const cleanUrl = `${window.location.pathname}${params.size ? `?${params}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", cleanUrl);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const credentials = {
      email: String(form.get("email") ?? "").trim(),
      password: String(form.get("password") ?? ""),
    };
    const native = isNativeApp();
    const payload =
      mode === "setup"
        ? {
            ...credentials,
            displayName: String(form.get("displayName") ?? "").trim(),
          }
        : { ...credentials, client: native ? "ios" as const : "web" as const };
    try {
      const result = await apiMutation<{
        user: User;
        csrfToken?: string;
        accessToken?: string;
        refreshToken?: string;
        expiresIn?: number;
      }>(
        `/api/v1/auth/${mode}`,
        "POST",
        payload,
      );
      if ("queued" in result) throw new Error("登入需要網路連線");
      if (native && result.accessToken && result.refreshToken && result.expiresIn) {
        await saveNativeTokens(result.accessToken, result.refreshToken, result.expiresIn);
        onAuthenticated(result.user, null);
      } else {
        onAuthenticated(result.user, result.csrfToken ?? null);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "無法登入");
    } finally {
      setBusy(false);
    }
  };

  const loginWithGoogle = async () => {
    setGoogleBusy(true);
    setError("");
    try {
      if (!isNativeApp()) {
        window.location.assign("/api/v1/auth/google/start");
        return;
      }
      const code = await beginNativeGoogleSignIn();
      const result = await apiMutation<{ user: User; accessToken: string; refreshToken: string; expiresIn: number }>(
        "/api/v1/auth/google/exchange", "POST", { code },
      );
      if ("queued" in result) throw new Error("Google 登入需要網路連線");
      await saveNativeTokens(result.accessToken, result.refreshToken, result.expiresIn);
      onAuthenticated(result.user, null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Google 登入失敗");
      setGoogleBusy(false);
    }
  };

  return (
    <div className="auth-layout">
      <section className="auth-story">
        <Brand />
        <div className="auth-promise">
          <span>你的節奏，不必和別人一樣。</span>
          <h1>
            把每天做的事，
            <br />
            變成看得見的進步。
          </h1>
          <p>一個地方，清楚管理體重、飲食、訓練與 Apple Watch 活動資料。</p>
        </div>
        <div className="auth-preview" aria-hidden="true">
          <div className="preview-card big">
            <span>本週平衡</span>
            <strong>
              82<small>%</small>
            </strong>
            <div className="mini-bars">
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
          </div>
          <div className="preview-card">
            <MoonStar />
            <b>7 小時 24 分</b>
            <span>昨晚睡眠</span>
          </div>
          <div className="preview-card accent">
            <TrendingDown />
            <b>−1.8 kg</b>
            <span>近 30 天趨勢</span>
          </div>
        </div>
      </section>
      <section className="auth-panel">
        <div className="mobile-brand">
          <Brand />
        </div>
        <div className="auth-card">
          <span className="eyebrow">
            {mode === "setup" ? "建立你的私人空間" : "歡迎回來"}
          </span>
          <h2>{mode === "setup" ? "開始第一天" : "登入衡日"}</h2>
          <p>
            {mode === "setup"
              ? "這個系統只允許建立一位擁有者。"
              : "繼續記錄屬於你的步調。"}
          </p>
          {googleSsoConfigured && (
            <>
              <button
                type="button"
                className="google-button"
                disabled={googleBusy}
                onClick={() => void loginWithGoogle()}
              >
                <span className="google-mark" aria-hidden="true">G</span>
                {googleBusy
                  ? "正在前往 Google…"
                  : ownerExists
                    ? "使用 Google 帳號登入"
                    : "使用 Google 建立 owner"}
              </button>
              <div className="auth-divider"><span>或使用電子郵件與密碼</span></div>
            </>
          )}
          <form onSubmit={submit}>
            {mode === "setup" && (
              <Field label="你的名字">
                <input
                  name="displayName"
                  autoComplete="name"
                  minLength={1}
                  maxLength={40}
                  required
                  placeholder="例如：小衡"
                />
              </Field>
            )}
            <Field label="電子郵件">
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
            </Field>
            <Field
              label="密碼"
              hint={mode === "setup" ? "至少 12 個字元" : undefined}
            >
              <input
                name="password"
                type="password"
                autoComplete={
                  mode === "setup" ? "new-password" : "current-password"
                }
                minLength={12}
                required
              />
            </Field>
            {error && (
              <div className="form-error" role="alert">
                {error}
              </div>
            )}
            <button className="primary-button wide" disabled={busy}>
              {busy ? (
                <RefreshCw className="spin" />
              ) : mode === "setup" ? (
                "建立並繼續"
              ) : (
                "登入"
              )}{" "}
              {!busy && <ArrowRight />}
            </button>
          </form>
          {ownerExists && (
            <button
              className="text-button"
              onClick={() => setMode(mode === "login" ? "setup" : "login")}
              disabled={mode === "login"}
            >
              {mode === "login" ? "這個空間已完成設定" : "返回登入"}
            </button>
          )}
          <p className="privacy-note">
            <ShieldCheck /> 你的健康資料不會用於廣告或公開分享。
          </p>
        </div>
      </section>
    </div>
  );
}

function Onboarding({
  user,
  onComplete,
  notify,
}: {
  user: User;
  onComplete: (user: User) => void;
  notify: (message: string, tone?: Toast["tone"]) => void;
}) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    goalType: "fat_loss" as GoalType,
    heightCm: 170,
    currentWeightKg: 78,
    targetWeightKg: 70,
    activityLevel: "light",
    dailyKcal: 1900,
    proteinG: 120,
    carbsG: 210,
    fatG: 60,
    waterMl: 2000,
    weeklyWorkouts: 3,
  });
  const patch = (values: Partial<typeof form>) =>
    setForm((state) => ({ ...state, ...values }));

  const finish = async () => {
    setBusy(true);
    try {
      const result = await apiMutation<{ profile: Profile; user: User }>(
        "/api/v1/profile",
        "PUT",
        form,
      );
      if ("queued" in result) throw new Error("初始設定需要網路連線");
      notify("你的衡日空間準備好了");
      onComplete(result.user);
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法儲存設定", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="onboarding">
      <header>
        <Brand />
        <span>步驟 {step} / 3</span>
      </header>
      <div className="onboarding-progress">
        <i style={{ width: `${(step / 3) * 100}%` }} />
      </div>
      <main>
        {step === 1 && (
          <section className="onboarding-card">
            <span className="step-icon">
              <Target />
            </span>
            <span className="eyebrow">嗨，{user.displayName}</span>
            <h1>你現在最想完成什麼？</h1>
            <p>這只會調整儀表板的重點，之後隨時能改。</p>
            <div className="choice-grid">
              {(
                [
                  [
                    "fat_loss",
                    "穩定減脂",
                    "看見體重平均與飲食節奏",
                    TrendingDown,
                  ],
                  ["muscle_gain", "增肌變強", "聚焦訓練量與營養補充", Dumbbell],
                  ["maintain", "維持平衡", "兼顧活動、睡眠與日常", Sparkles],
                ] as const
              ).map(([value, title, description, Icon]) => (
                <button
                  key={value}
                  className={`choice-card ${form.goalType === value ? "selected" : ""}`}
                  onClick={() => patch({ goalType: value })}
                >
                  <Icon />
                  <strong>{title}</strong>
                  <span>{description}</span>
                  {form.goalType === value && (
                    <Check className="choice-check" />
                  )}
                </button>
              ))}
            </div>
          </section>
        )}
        {step === 2 && (
          <section className="onboarding-card">
            <span className="step-icon">
              <Weight />
            </span>
            <span className="eyebrow">你的起點</span>
            <h1>先記下現在的位置</h1>
            <p>所有數字都能修改，也可以稍後由 Apple Health 更新。</p>
            <div className="form-grid three">
              <NumberField
                label="身高"
                value={form.heightCm}
                unit="cm"
                min={100}
                max={250}
                onChange={(heightCm) => patch({ heightCm })}
              />
              <NumberField
                label="目前體重"
                value={form.currentWeightKg}
                unit="kg"
                min={30}
                max={350}
                step={0.1}
                onChange={(currentWeightKg) => patch({ currentWeightKg })}
              />
              <NumberField
                label="目標體重"
                value={form.targetWeightKg}
                unit="kg"
                min={30}
                max={350}
                step={0.1}
                onChange={(targetWeightKg) => patch({ targetWeightKg })}
              />
            </div>
            <Field label="平常活動程度">
              <select
                value={form.activityLevel}
                onChange={(event) =>
                  patch({ activityLevel: event.target.value })
                }
              >
                <option value="sedentary">久坐為主</option>
                <option value="light">輕度活動</option>
                <option value="moderate">中度活動</option>
                <option value="active">高度活動</option>
              </select>
            </Field>
          </section>
        )}
        {step === 3 && (
          <section className="onboarding-card">
            <span className="step-icon">
              <Leaf />
            </span>
            <span className="eyebrow">每日目標</span>
            <h1>確認你的第一版目標</h1>
            <p>這些是起始值，不是醫療處方；請依實際需求調整。</p>
            <div className="goal-summary">
              <NumberField
                label="每日熱量"
                value={form.dailyKcal}
                unit="kcal"
                min={800}
                max={5000}
                step={50}
                onChange={(dailyKcal) => patch({ dailyKcal })}
              />
              <NumberField
                label="蛋白質"
                value={form.proteinG}
                unit="g"
                min={20}
                max={400}
                onChange={(proteinG) => patch({ proteinG })}
              />
              <NumberField
                label="碳水"
                value={form.carbsG}
                unit="g"
                min={20}
                max={700}
                onChange={(carbsG) => patch({ carbsG })}
              />
              <NumberField
                label="脂肪"
                value={form.fatG}
                unit="g"
                min={10}
                max={300}
                onChange={(fatG) => patch({ fatG })}
              />
            </div>
            <div className="soft-note">
              <Info />{" "}
              衡日不會因為活動熱量而自動增加飲食預算，也不會自行修改你的目標。
            </div>
          </section>
        )}
        <footer>
          <button
            className="secondary-button"
            onClick={() => setStep((value) => Math.max(1, value - 1))}
            disabled={step === 1}
          >
            <ChevronLeft /> 上一步
          </button>
          {step < 3 ? (
            <button
              className="primary-button"
              onClick={() => setStep((value) => value + 1)}
            >
              繼續 <ArrowRight />
            </button>
          ) : (
            <button className="primary-button" onClick={finish} disabled={busy}>
              {busy ? <RefreshCw className="spin" /> : <Check />} 完成設定
            </button>
          )}
        </footer>
      </main>
    </div>
  );
}

function SideRail({
  active,
  onChange,
  user,
}: {
  active: Tab;
  onChange: (tab: Tab) => void;
  user: User;
}) {
  const tabs: Array<[Tab, string, typeof Home]> = [
    ["today", "今日", Home],
    ["food", "飲食", Utensils],
    ["training", "訓練", Dumbbell],
    ["trends", "趨勢", BarChart3],
    ["settings", "設定", Settings],
  ];
  return (
    <aside className="side-rail">
      <Brand />{" "}
      <nav aria-label="主要導覽">
        {tabs.map(([id, label, Icon]) => (
          <button
            key={id}
            className={active === id ? "active" : ""}
            onClick={() => onChange(id)}
            aria-current={active === id ? "page" : undefined}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="side-profile">
        <span>{user.displayName.slice(0, 1)}</span>
        <div>
          <strong>{user.displayName}</strong>
          <small>{goalLabels.fat_loss}計畫</small>
        </div>
        <MoreHorizontal />
      </div>
    </aside>
  );
}

function BottomNav({
  active,
  onChange,
}: {
  active: Tab;
  onChange: (tab: Tab) => void;
}) {
  const tabs: Array<[Tab, string, typeof Home]> = [
    ["today", "今日", Home],
    ["food", "飲食", Utensils],
    ["training", "訓練", Dumbbell],
    ["trends", "趨勢", BarChart3],
    ["settings", "設定", Settings],
  ];
  return (
    <nav className="bottom-nav" aria-label="主要導覽">
      {tabs.map(([id, label, Icon]) => (
        <button
          key={id}
          className={active === id ? "active" : ""}
          onClick={() => onChange(id)}
        >
          <Icon />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function TopBar({
  user,
  online,
  tab,
  onLogout,
}: {
  user: User;
  online: boolean;
  tab: Tab;
  onLogout: () => void;
}) {
  const labels: Record<Tab, string> = {
    today: "今日",
    food: "飲食管理",
    training: "訓練管理",
    trends: "趨勢分析",
    settings: "設定",
  };
  const [menu, setMenu] = useState(false);
  return (
    <header className="top-bar">
      <div>
        <button className="mobile-menu" aria-label="開啟導覽">
          <Menu />
        </button>
        <span>{labels[tab]}</span>
      </div>
      <div className="top-actions">
        <span className={`online-state ${online ? "" : "offline"}`}>
          {online ? <Cloud /> : <CloudOff />}
          {online ? "已同步" : "離線模式"}
        </span>
        <button
          className="avatar-button"
          onClick={() => setMenu(!menu)}
          aria-label="帳號選單"
        >
          {user.displayName.slice(0, 1)}
          <ChevronDown />
        </button>
        {menu && (
          <div className="account-menu">
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
            <button onClick={onLogout}>
              <LogOut /> 登出
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function TodayPage({
  notify,
  navigate,
}: {
  notify: (message: string, tone?: Toast["tone"]) => void;
  navigate: (tab: Tab) => void;
}) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [measurementOpen, setMeasurementOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await apiGet<DashboardData>(`/api/v1/dashboard?date=${today()}`, {
          cacheKey: `dashboard:${today()}`,
        }),
      );
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "無法載入今日資料",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => {
    void load();
  }, [load]);
  if (loading && !data) return <PageSkeleton />;
  if (!data)
    return (
      <EmptyState
        icon={<WifiOff />}
        title="暫時讀不到今天的資料"
        action={
          <button className="primary-button" onClick={load}>
            重新整理
          </button>
        }
      />
    );
  const caloriePercent = percent(
    data.nutrition.calories,
    data.profile.dailyKcal,
  );
  const latestWeight =
    data.measurement?.weightKg ?? data.profile.currentWeightKg;
  return (
    <div className="page today-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">{formatChineseDate(data.date)}</span>
          <h1>{data.greeting}</h1>
          <p>不必完美，把今天重要的事看清楚就好。</p>
        </div>
        <button className="date-button">
          <CalendarDays /> 今天 <ChevronDown />
        </button>
      </section>
      <section className="hero-grid">
        <article className="card balance-card">
          <div className="card-header">
            <div>
              <span className="eyebrow light">今日平衡</span>
              <h2>你的節奏正慢慢成形</h2>
            </div>
            <span className="streak">
              <Flame /> {data.streakDays} 天
            </span>
          </div>
          <div className="balance-body">
            <ProgressRing
              value={Math.min(
                100,
                Math.round(
                  (caloriePercent +
                    percent(data.health.steps, 8000) +
                    percent(data.waterMl, data.profile.waterMl)) /
                    3,
                ),
              )}
            />
            <div className="balance-stats">
              <MetricMini
                icon={<Utensils />}
                label="飲食"
                value={`${Math.round(data.nutrition.calories)}`}
                unit={`/ ${data.profile.dailyKcal} kcal`}
              />
              <MetricMini
                icon={<Activity />}
                label="步數"
                value={data.health.steps.toLocaleString()}
                unit="步"
              />
              <MetricMini
                icon={<Droplets />}
                label="喝水"
                value={data.waterMl.toLocaleString()}
                unit={`/ ${data.profile.waterMl} ml`}
              />
            </div>
          </div>
          <p className="balance-message">
            <Sparkles />{" "}
            {data.observations[0] ?? "每一筆紀錄，都是更了解自己的線索。"}
          </p>
        </article>
        <article className="card weight-card">
          <div className="card-header">
            <div>
              <span className="eyebrow">體重趨勢</span>
              <h3>
                {latestWeight ? `${latestWeight.toFixed(1)} kg` : "尚未記錄"}
              </h3>
            </div>
            <button
              className="icon-button"
              onClick={() => setMeasurementOpen(true)}
              aria-label="新增體重"
            >
              <Plus />
            </button>
          </div>
          <WeightChart points={data.weightTrend} />
          <div className="trend-footer">
            <span>7 日平均</span>
            <strong>
              {data.weightTrend.at(-1)?.average
                ? `${data.weightTrend.at(-1)!.average.toFixed(1)} kg`
                : "—"}
            </strong>
          </div>
        </article>
      </section>
      <section className="section-heading">
        <div>
          <span className="eyebrow">今天的四件事</span>
          <h2>把注意力放在能行動的地方</h2>
        </div>
      </section>
      <section className="metric-grid">
        <MetricCard
          icon={<Utensils />}
          tone="green"
          label="飲食"
          value={`${Math.round(data.nutrition.calories)}`}
          unit="kcal"
          progress={caloriePercent}
          detail={`蛋白質 ${Math.round(data.nutrition.proteinG)} / ${data.profile.proteinG} g`}
          onClick={() => navigate("food")}
        />
        <MetricCard
          icon={<Activity />}
          tone="gold"
          label="日常活動"
          value={data.health.steps.toLocaleString()}
          unit="步"
          progress={percent(data.health.steps, 8000)}
          detail={`活動消耗 ${Math.round(data.health.activeKcal)} kcal`}
        />
        <MetricCard
          icon={<MoonStar />}
          tone="blue"
          label="昨晚睡眠"
          value={
            data.health.sleepMinutes
              ? `${Math.floor(data.health.sleepMinutes / 60)}:${String(data.health.sleepMinutes % 60).padStart(2, "0")}`
              : "—"
          }
          unit="小時"
          progress={percent(data.health.sleepMinutes, 480)}
          detail={
            data.health.restingHeartRate
              ? `靜息心率 ${Math.round(data.health.restingHeartRate)} bpm`
              : "等待 Apple Health 資料"
          }
        />
        <MetricCard
          icon={<Dumbbell />}
          tone="purple"
          label="今日訓練"
          value={data.workout ? `${data.workout.durationMinutes}` : "—"}
          unit={data.workout ? "分鐘" : ""}
          progress={data.workout ? 100 : 0}
          detail={data.workout?.name ?? "安排一段屬於自己的時間"}
          onClick={() => navigate("training")}
        />
      </section>
      <section className="quick-card card">
        <div>
          <div className="eyebrow">快速記錄</div>
          <h2>剛完成什麼？</h2>
        </div>
        <div className="quick-actions">
          <button onClick={() => setMeasurementOpen(true)}>
            <span>
              <Weight />
            </span>
            體重
          </button>
          <button onClick={() => navigate("food")}>
            <span>
              <Utensils />
            </span>
            餐點
          </button>
          <button onClick={() => navigate("training")}>
            <span>
              <Dumbbell />
            </span>
            訓練
          </button>
          <button onClick={() => navigate("food")}>
            <span>
              <Droplets />
            </span>
            喝水
          </button>
        </div>
      </section>
      {measurementOpen && (
        <MeasurementModal
          onClose={() => setMeasurementOpen(false)}
          onSaved={() => {
            setMeasurementOpen(false);
            void load();
            notify("體重紀錄已儲存");
          }}
        />
      )}
    </div>
  );
}

function FoodPage({
  notify,
}: {
  notify: (message: string, tone?: Toast["tone"]) => void;
}) {
  const [date, setDate] = useState(today());
  const [day, setDay] = useState<MealDay | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [foods, setFoods] = useState<Food[]>([]);
  const [frequentMeals, setFrequentMeals] = useState<FrequentMeal[]>([]);
  const [query, setQuery] = useState("");
  const [browseMode, setBrowseMode] = useState<"search" | "favorites" | "frequent">("search");
  const [selectedFood, setSelectedFood] = useState<Food | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [hydrationOpen, setHydrationOpen] = useState(false);
  const [mealType, setMealType] = useState<MealType>("breakfast");

  const loadDay = useCallback(async () => {
    try {
      const [meal, profileResult] = await Promise.all([
        apiGet<MealDay>(`/api/v1/meals?date=${date}`, {
          cacheKey: `meals:${date}`,
        }),
        apiGet<{ profile: Profile }>("/api/v1/profile", {
          cacheKey: "profile",
        }),
      ]);
      setDay(meal);
      setProfile(profileResult.profile);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "無法載入飲食紀錄",
        "error",
      );
    }
  }, [date, notify]);

  useEffect(() => {
    void loadDay();
  }, [loadDay]);
  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        if (browseMode === "frequent") {
          const result = await apiGet<{ meals: FrequentMeal[] }>("/api/v1/meals/frequent", { signal: controller.signal });
          setFrequentMeals(result.meals);
          return;
        }
        setFoods(
          (
            await apiGet<{ foods: Food[] }>(
              `/api/v1/foods?q=${encodeURIComponent(query)}${browseMode === "favorites" ? "&favorite=1" : ""}`,
              { signal: controller.signal },
            )
          ).foods,
        );
      } catch (error) {
        if (!(error instanceof DOMException))
          notify("食物搜尋暫時無法使用", "error");
      }
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [browseMode, query, notify]);

  const previousDay = () => setDate(shiftDate(date, -1));
  const nextDay = () => setDate(shiftDate(date, 1));
  const removeEntry = async (entry: MealEntry) => {
    try {
      await apiMutation(`/api/v1/meals/${entry.id}`, "DELETE", undefined, {
        queueOffline: true,
        version: entry.version,
      });
      await loadDay();
      notify("餐點已移除");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法移除", "error");
    }
  };
  const copyYesterday = async () => {
    try {
      await apiMutation(
        "/api/v1/meals/copy",
        "POST",
        { from: shiftDate(date, -1), to: date },
        { queueOffline: true },
      );
      await loadDay();
      notify("已複製昨日餐點");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法複製", "error");
    }
  };
  const addWater = async (amountMl: number) => {
    try {
      await apiMutation(
        "/api/v1/hydration",
        "POST",
        { id: crypto.randomUUID(), localDate: date, amountMl },
        { queueOffline: true },
      );
      await loadDay();
      notify(`已加入 ${amountMl} ml`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法新增飲水", "error");
    }
  };
  const toggleFavorite = async (food: Food) => {
    const favorite = !food.isFavorite;
    try {
      await apiMutation(`/api/v1/foods/${food.id}/favorite`, "PUT", { favorite }, { queueOffline: true });
      setFoods((items) => items.map((item) => item.id === food.id ? { ...item, isFavorite: favorite } : item));
      notify(favorite ? "已加入收藏食品" : "已移除收藏");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法更新收藏", "error");
    }
  };
  const addFrequentMeal = async (meal: FrequentMeal) => {
    try {
      await apiMutation("/api/v1/meals", "POST", {
        id: crypto.randomUUID(), localDate: date, mealType: meal.mealType,
        foodId: meal.foodId, foodName: meal.foodName, amountG: meal.amountG,
        calories: meal.calories, proteinG: meal.proteinG, carbsG: meal.carbsG,
        fatG: meal.fatG, fiberG: meal.fiberG,
      }, { queueOffline: true });
      await loadDay();
      notify(`已加入常用餐點：${meal.foodName}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法加入常用餐點", "error");
    }
  };
  if (!day || !profile) return <PageSkeleton />;
  return (
    <div className="page food-page">
      <section className="page-heading compact-heading">
        <div>
          <span className="eyebrow">飲食管理</span>
          <h1>吃得清楚，也吃得自在</h1>
          <p>記錄是為了理解，不是為了責備。</p>
        </div>
        <DateStepper date={date} previous={previousDay} next={nextDay} />
      </section>
      <section className="nutrition-overview card">
        <div className="calorie-orbit">
          <ProgressRing
            value={percent(day.totals.calories, profile.dailyKcal)}
            compact
          />
          <div>
            <strong>
              {Math.round(profile.dailyKcal - day.totals.calories)}
            </strong>
            <span>kcal 可用</span>
          </div>
        </div>
        <div className="macro-grid">
          <Macro
            label="蛋白質"
            value={day.totals.proteinG}
            target={profile.proteinG}
            color="#a85f51"
          />
          <Macro
            label="碳水"
            value={day.totals.carbsG}
            target={profile.carbsG}
            color="#9a7535"
          />
          <Macro
            label="脂肪"
            value={day.totals.fatG}
            target={profile.fatG}
            color="#746b82"
          />
        </div>
        <div className="water-block">
          <Droplets />
          <div>
            <span>飲水</span>
            <strong>
              {day.waterMl} / {profile.waterMl} ml
            </strong>
          </div>
          <div className="water-actions">
            <button onClick={() => setHydrationOpen(true)}>歷史</button>
            <button onClick={() => void addWater(250)}><Plus /> 250</button>
          </div>
        </div>
      </section>
      <section className="food-workspace">
        <div className="meal-column">
          <div className="section-heading inline">
            <div>
              <span className="eyebrow">今日餐點</span>
              <h2>已記錄的內容</h2>
            </div>
            <button
              className="secondary-button small"
              onClick={() => void copyYesterday()}
            >
              <RefreshCw /> 複製昨日
            </button>
          </div>
          {(Object.keys(mealLabels) as MealType[]).map((type) => (
            <MealGroup
              key={type}
              type={type}
              entries={day.entries.filter((entry) => entry.mealType === type)}
              onAdd={() => {
                setMealType(type);
                document.getElementById("food-search")?.focus();
              }}
              onRemove={removeEntry}
            />
          ))}
        </div>
        <aside className="food-search card">
          <div className="card-header">
            <div>
              <span className="eyebrow">食物資料庫</span>
              <h2>新增到 {mealLabels[mealType]}</h2>
            </div>
            <button
              className="icon-button"
              onClick={() => setCustomOpen(true)}
              aria-label="建立自訂食物"
            >
              <Plus />
            </button>
          </div>
          <label className="search-box">
            <Search />
            <input
              id="food-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜尋白飯、雞胸肉…"
              disabled={browseMode === "frequent"}
            />
          </label>
          <div className="food-tabs" role="tablist" aria-label="食品瀏覽方式">
            {([['search','搜尋'],['favorites','收藏食品'],['frequent','常用餐點']] as const).map(([value, label]) => (
              <button key={value} role="tab" aria-selected={browseMode === value} className={browseMode === value ? "active" : ""} onClick={() => setBrowseMode(value)}>{label}</button>
            ))}
          </div>
          <div className="food-results">
            {browseMode !== "frequent" && foods.map((food) => (
              <div key={food.id} className="food-result">
                <button className="food-result-main" onClick={() => setSelectedFood(food)}>
                  <span className="food-avatar">{food.name.slice(0, 1)}</span>
                  <div><strong>{food.name}</strong><small>{food.servingName} · {Math.round((food.kcal100g * food.servingG) / 100)} kcal</small></div>
                  <Plus />
                </button>
                <button className={`favorite-button ${food.isFavorite ? "active" : ""}`} aria-label={food.isFavorite ? `取消收藏 ${food.name}` : `收藏 ${food.name}`} onClick={() => void toggleFavorite(food)}>
                  <Star fill={food.isFavorite ? "currentColor" : "none"} />
                </button>
              </div>
            ))}
            {browseMode === "frequent" && frequentMeals.map((meal) => (
              <button key={`${meal.foodId ?? meal.foodName}-${meal.mealType}`} className="food-result frequent-meal" onClick={() => void addFrequentMeal(meal)}>
                <span className="food-avatar">{meal.foodName.slice(0,1)}</span>
                <div><strong>{meal.foodName}</strong><small>{mealLabels[meal.mealType]} · {meal.amountG} g · 使用 {meal.uses} 次</small></div>
                <Plus />
              </button>
            ))}
            {((browseMode !== "frequent" && !foods.length) || (browseMode === "frequent" && !frequentMeals.length)) && (
              <div className="empty-search">
                {browseMode === "favorites" ? <Star /> : <Search />}
                <span>{browseMode === "favorites" ? "尚未收藏食品" : browseMode === "frequent" ? "累積餐點紀錄後會自動整理" : "找不到相符食物"}</span>
                {browseMode === "search" && <button onClick={() => setCustomOpen(true)}>建立自訂食物</button>}
              </div>
            )}
          </div>
          <p className="source-note">
            <Info />{" "}
            參考食藥署食品營養成分資料集；實際數值依品牌與料理方式而異。
          </p>
        </aside>
      </section>
      {selectedFood && (
        <FoodEntryModal
          food={selectedFood}
          date={date}
          mealType={mealType}
          onClose={() => setSelectedFood(null)}
          onSaved={() => {
            setSelectedFood(null);
            void loadDay();
            notify("已加入餐點");
          }}
        />
      )}
      {customOpen && (
        <CustomFoodModal
          onClose={() => setCustomOpen(false)}
          onSaved={(food) => {
            setCustomOpen(false);
            setFoods((items) => [food, ...items]);
            setSelectedFood(food);
            notify("自訂食物已建立");
          }}
        />
      )}
      {hydrationOpen && (
        <HydrationHistoryModal
          onClose={() => setHydrationOpen(false)}
          notify={notify}
          onChanged={() => void loadDay()}
        />
      )}
    </div>
  );
}

function TrainingPage({
  notify,
}: {
  notify: (message: string, tone?: Toast["tone"]) => void;
}) {
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkoutTemplate | null>(null);
  const load = useCallback(async () => {
    try {
      const [templateResult, sessionResult] = await Promise.all([
        apiGet<{ templates: WorkoutTemplate[] }>("/api/v1/workout-templates"),
        apiGet<{ sessions: WorkoutSession[] }>("/api/v1/workouts?range=30", {
          cacheKey: "workouts:30",
        }),
      ]);
      setTemplates(templateResult.templates);
      setSessions(sessionResult.sessions);
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法載入訓練", "error");
    }
  }, [notify]);
  useEffect(() => {
    void load();
  }, [load]);
  const deleteTemplate = async (template: WorkoutTemplate) => {
    if (!window.confirm(`確定刪除「${template.name}」模板？`)) return;
    try {
      await apiMutation(`/api/v1/workout-templates/${template.id}`, "DELETE", undefined, { queueOffline: true, version: template.version });
      setTemplates((items) => items.filter((item) => item.id !== template.id));
      notify("訓練模板已刪除");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法刪除模板", "error");
    }
  };
  const thisWeek = sessions.filter(
    (session) => daysBetween(session.localDate, today()) < 7,
  );
  const volume = thisWeek.reduce(
    (sum, session) => sum + workoutVolume(session.sets),
    0,
  );
  return (
    <div className="page training-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">訓練管理</span>
          <h1>下一次，比上次多一點</h1>
          <p>記住完成的每一組，也替休息留下空間。</p>
        </div>
        <button className="primary-button" onClick={() => setSessionOpen(true)}>
          <Plus /> 開始記錄
        </button>
      </section>
      <section className="training-summary">
        <article className="card">
          <span className="metric-icon green">
            <Dumbbell />
          </span>
          <div>
            <span>本週訓練</span>
            <strong>
              {thisWeek.length}
              <small> 次</small>
            </strong>
          </div>
        </article>
        <article className="card">
          <span className="metric-icon gold">
            <Activity />
          </span>
          <div>
            <span>總訓練量</span>
            <strong>
              {Math.round(volume).toLocaleString()}
              <small> kg</small>
            </strong>
          </div>
        </article>
        <article className="card">
          <span className="metric-icon purple">
            <Trophy />
          </span>
          <div>
            <span>本週時間</span>
            <strong>
              {thisWeek.reduce((sum, item) => sum + item.durationMinutes, 0)}
              <small> 分鐘</small>
            </strong>
          </div>
        </article>
      </section>
      <section className="training-layout">
        <div>
          <div className="section-heading inline">
            <div>
              <span className="eyebrow">最近訓練</span>
              <h2>看見你做過的努力</h2>
            </div>
          </div>
          <div className="session-list">
            {sessions.map((session) => (
              <article className="session-card card" key={session.id}>
                <div className="session-date">
                  <strong>
                    {new Date(`${session.localDate}T00:00:00`).getDate()}
                  </strong>
                  <span>
                    {new Intl.DateTimeFormat("zh-TW", {
                      month: "short",
                    }).format(new Date(`${session.localDate}T00:00:00`))}
                  </span>
                </div>
                <div className="session-main">
                  <div>
                    <h3>{session.name}</h3>
                    <span>
                      {session.source === "healthkit"
                        ? "Apple Watch 匯入"
                        : `${session.sets.length} 組完成`}
                    </span>
                  </div>
                  <div className="session-meta">
                    <span>
                      <Activity /> {formatDuration(session.durationMinutes)}
                    </span>
                    {session.sets.length > 0 && (
                      <span>
                        <Weight />{" "}
                        {Math.round(
                          workoutVolume(session.sets),
                        ).toLocaleString()}{" "}
                        kg
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight />
              </article>
            ))}
            {!sessions.length && (
              <EmptyState
                icon={<Dumbbell />}
                title="還沒有訓練紀錄"
                description="完成第一場訓練後，進度會出現在這裡。"
                action={
                  <button
                    className="primary-button"
                    onClick={() => setSessionOpen(true)}
                  >
                    記錄第一場
                  </button>
                }
              />
            )}
          </div>
        </div>
        <aside>
          <div className="section-heading inline">
            <div>
              <span className="eyebrow">訓練模板</span>
              <h2>讓開始更容易</h2>
            </div>
            <button
              className="icon-button"
              aria-label="建立訓練模板"
              onClick={() => { setEditingTemplate(null); setTemplateOpen(true); }}
            >
              <Plus />
            </button>
          </div>
          <div className="template-list">
            {templates.map((template) => (
              <article className="template-card card" key={template.id}>
                <span>
                  <Dumbbell />
                </span>
                <button className="template-start" onClick={() => setSessionOpen(true)}>
                  <strong>{template.name}</strong>
                  <small>{template.items.length} 個動作</small>
                </button>
                <div className="template-actions">
                  <button className="icon-button" aria-label={`編輯 ${template.name}`} onClick={() => { setEditingTemplate(template); setTemplateOpen(true); }}><Pencil /></button>
                  <button className="icon-button danger" aria-label={`刪除 ${template.name}`} onClick={() => void deleteTemplate(template)}><Trash2 /></button>
                </div>
              </article>
            ))}
            {!templates.length && (
              <button
                className="template-placeholder"
                onClick={() => { setEditingTemplate(null); setTemplateOpen(true); }}
              >
                <Plus />
                <strong>建立第一個模板</strong>
                <span>把常做的動作排好</span>
              </button>
            )}
          </div>
        </aside>
      </section>
      {sessionOpen && (
        <WorkoutSessionModal
          onClose={() => setSessionOpen(false)}
          onSaved={() => {
            setSessionOpen(false);
            void load();
            notify("訓練紀錄已儲存");
          }}
        />
      )}
      {templateOpen && (
        <WorkoutTemplateModal
          template={editingTemplate}
          onClose={() => setTemplateOpen(false)}
          onSaved={() => {
            setTemplateOpen(false);
            void load();
            notify(editingTemplate ? "訓練模板已更新" : "訓練模板已建立");
          }}
        />
      )}
    </div>
  );
}

function TrendsPage({
  notify,
}: {
  notify: (message: string, tone?: Toast["tone"]) => void;
}) {
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  useEffect(() => {
    Promise.all([
      apiGet<{ measurements: Measurement[] }>("/api/v1/measurements?range=90", {
        cacheKey: "measurements:90",
      }),
      apiGet<{ sessions: WorkoutSession[] }>("/api/v1/workouts?range=90", {
        cacheKey: "workouts:90",
      }),
      apiGet<DashboardData>(`/api/v1/dashboard?date=${today()}`, {
        cacheKey: `dashboard:${today()}`,
      }),
    ])
      .then(([measurementResult, workoutResult, dashboardResult]) => {
        setMeasurements(measurementResult.measurements);
        setSessions(workoutResult.sessions);
        setDashboard(dashboardResult);
      })
      .catch((error: Error) => notify(error.message, "error"));
  }, [notify]);
  const weights = measurements
    .filter((item) => item.weightKg !== null)
    .sort((a, b) => a.localDate.localeCompare(b.localDate));
  const first = weights[0]?.weightKg;
  const last = weights.at(-1)?.weightKg;
  const change = first && last ? last - first : null;
  const weekly = Array.from({ length: 12 }, (_, index) => {
    const end = new Date();
    end.setDate(end.getDate() - (11 - index) * 7);
    return sessions.filter(
      (session) =>
        Math.abs(daysBetween(session.localDate, localDate(end))) < 7 &&
        session.localDate <= localDate(end),
    ).length;
  });
  return (
    <div className="page trends-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">90 天趨勢</span>
          <h1>看方向，不被單日數字牽著走</h1>
          <p>衡日用平均與長期變化，幫你看見真正的節奏。</p>
        </div>
        <button className="secondary-button">
          <CalendarDays /> 最近 90 天
        </button>
      </section>
      <section className="trend-kpis">
        <article className="card">
          <span>體重變化</span>
          <strong>
            {change === null
              ? "—"
              : `${change > 0 ? "+" : ""}${change.toFixed(1)} kg`}
          </strong>
          <small>{weights.length} 次紀錄</small>
        </article>
        <article className="card">
          <span>訓練次數</span>
          <strong>{sessions.length} 次</strong>
          <small>
            累積 {sessions.reduce((sum, item) => sum + item.durationMinutes, 0)}{" "}
            分鐘
          </small>
        </article>
        <article className="card">
          <span>目前連續</span>
          <strong>{dashboard?.streakDays ?? 0} 天</strong>
          <small>有記錄的日子</small>
        </article>
      </section>
      <section className="trend-charts">
        <article className="card large-chart">
          <div className="card-header">
            <div>
              <span className="eyebrow">體重與 7 日平均</span>
              <h2>{last ? `${last.toFixed(1)} kg` : "等待更多資料"}</h2>
            </div>
            <span className="legend">
              <i /> 日平均
            </span>
          </div>
          <LargeWeightChart measurements={measurements} />
        </article>
        <article className="card">
          <div className="card-header">
            <div>
              <span className="eyebrow">每週訓練</span>
              <h2>穩定度</h2>
            </div>
          </div>
          <BarMiniChart values={weekly} />
          <div className="chart-insight">
            <Sparkles />
            <p>
              {sessions.length
                ? "持續出現，比單次做很多更有價值。"
                : "從一場容易完成的訓練開始。"}
            </p>
          </div>
        </article>
      </section>
      <section className="card observations">
        <div>
          <span className="eyebrow">衡日觀察</span>
          <h2>這些是資料的描述，不是評分</h2>
        </div>
        <div>
          {dashboard?.observations.map((item) => (
            <p key={item}>
              <Check /> {item}
            </p>
          )) ?? (
            <p>
              <Info /> 累積更多紀錄後，這裡會出現趨勢摘要。
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function SettingsPage({
  user,
  notify,
  onLogout,
  onUserChange,
}: {
  user: User;
  notify: (message: string, tone?: Toast["tone"]) => void;
  onLogout: () => void;
  onUserChange: (user: User) => void;
}) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [healthAvailable, setHealthAvailable] = useState(false);
  const [lastHealthSync, setLastHealthSync] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  useEffect(() => {
    void apiGet<{ profile: Profile }>("/api/v1/profile", {
      cacheKey: "profile",
    }).then((result) => setProfile(result.profile));
    void apiGet<DashboardData>(`/api/v1/dashboard?date=${today()}`, {
      cacheKey: `dashboard:${today()}`,
    }).then((result) => setLastHealthSync(result.sync.lastSyncedAt));
    void healthKitAvailability().then(setHealthAvailable);
  }, []);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!profile) return;
    try {
      const result = await apiMutation<{ profile: Profile; user: User }>(
        "/api/v1/profile",
        "PUT",
        profile,
        { version: profile.version },
      );
      if ("queued" in result) return notify("設定已排入離線同步", "info");
      setProfile(result.profile);
      onUserChange(result.user);
      notify("設定已更新");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法更新設定", "error");
    }
  };
  const syncHealth = async () => {
    if (!healthAvailable)
      return notify("請在 iPhone App 中使用 Apple Health 同步", "info");
    setSyncing(true);
    try {
      const authorization = await authorizeHealthKit();
      if (!authorization.granted.length)
        return notify("尚未授權任何健康資料", "info");
      localStorage.setItem("hengri:healthkit-enabled", "true");
      const days = lastHealthSync ? 7 : 30;
      const batch = await collectHealthKit(days, user.timezone);
      if (!batch.daily.length && !batch.workouts.length)
        return notify("這段期間沒有可匯入的健康資料", "info");
      const result = await apiMutation<{ accepted: number; skipped: number }>(
        "/api/v1/health/import",
        "POST",
        batch,
      );
      if ("queued" in result) return notify("健康資料需要網路才能同步", "info");
      setLastHealthSync(new Date().toISOString());
      notify(`已同步 ${result.accepted} 筆健康資料`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "同步失敗", "error");
    } finally {
      setSyncing(false);
    }
  };
  const exportData = async (format: "json" | "csv") => {
    try {
      await apiDownload(`/api/v1/export?format=${format}`);
      notify(`${format.toUpperCase()} 匯出已開始`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法匯出資料", "error");
    }
  };
  if (!profile) return <PageSkeleton />;
  return (
    <div className="page settings-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">個人設定</span>
          <h1>讓衡日貼近你的生活</h1>
          <p>目標是參考方向，隨著生活改變也可以調整。</p>
        </div>
      </section>
      <div className="settings-layout">
        <div className="settings-main">
          <section className="card settings-section">
            <div className="settings-title">
              <span>
                <CircleUserRound />
              </span>
              <div>
                <h2>個人與目標</h2>
                <p>{user.email}</p>
              </div>
            </div>
            <form onSubmit={save}>
              <div className="form-grid two">
                <Field label="目標類型">
                  <select
                    value={profile.goalType}
                    onChange={(event) =>
                      setProfile({
                        ...profile,
                        goalType: event.target.value as GoalType,
                      })
                    }
                  >
                    <option value="fat_loss">穩定減脂</option>
                    <option value="muscle_gain">增肌變強</option>
                    <option value="maintain">維持平衡</option>
                  </select>
                </Field>
                <Field label="活動程度">
                  <select
                    value={profile.activityLevel}
                    onChange={(event) =>
                      setProfile({
                        ...profile,
                        activityLevel: event.target
                          .value as Profile["activityLevel"],
                      })
                    }
                  >
                    <option value="sedentary">久坐為主</option>
                    <option value="light">輕度活動</option>
                    <option value="moderate">中度活動</option>
                    <option value="active">高度活動</option>
                  </select>
                </Field>
                <NumberField
                  label="每日熱量"
                  value={profile.dailyKcal}
                  unit="kcal"
                  min={800}
                  max={5000}
                  step={50}
                  onChange={(dailyKcal) =>
                    setProfile({ ...profile, dailyKcal })
                  }
                />
                <NumberField
                  label="蛋白質"
                  value={profile.proteinG}
                  unit="g"
                  min={20}
                  max={400}
                  onChange={(proteinG) => setProfile({ ...profile, proteinG })}
                />
                <NumberField
                  label="飲水目標"
                  value={profile.waterMl}
                  unit="ml"
                  min={500}
                  max={6000}
                  step={100}
                  onChange={(waterMl) => setProfile({ ...profile, waterMl })}
                />
                <NumberField
                  label="每週訓練"
                  value={profile.weeklyWorkouts}
                  unit="次"
                  min={0}
                  max={14}
                  onChange={(weeklyWorkouts) =>
                    setProfile({ ...profile, weeklyWorkouts })
                  }
                />
              </div>
              <button className="primary-button">
                <Check /> 儲存設定
              </button>
            </form>
          </section>
          <section className="card settings-section">
            <div className="settings-title">
              <span className="apple">
                <Apple />
              </span>
              <div>
                <h2>Apple Health</h2>
                <p>步數、活動、睡眠、體重與運動</p>
              </div>
              <span
                className={`status-pill ${healthAvailable ? "connected" : ""}`}
              >
                {healthAvailable ? "可連接" : "需使用 iPhone App"}
              </span>
            </div>
            <div className="health-types">
              <span>
                <Activity /> 步數與熱量
              </span>
              <span>
                <MoonStar /> 睡眠
              </span>
              <span>
                <HeartPulse /> 心率
              </span>
              <span>
                <Weight /> 體重與體脂
              </span>
            </div>
            <button
              className="secondary-button"
              onClick={() => void syncHealth()}
              disabled={!healthAvailable || syncing}
            >
              {syncing ? <RefreshCw className="spin" /> : <RefreshCw />}{" "}
              同步最近 {lastHealthSync ? 7 : 30} 天
            </button>
            <p className="source-note">
              <ShieldCheck /> 只匯入每日彙總，不儲存分鐘級原始心率；你可隨時在
              iPhone 撤銷權限。
            </p>
          </section>
          <section className="card settings-section">
            <div className="settings-title">
              <span>
                <Download />
              </span>
              <div>
                <h2>資料匯出</h2>
                <p>帶走你的完整紀錄</p>
              </div>
            </div>
            <div className="button-row">
              <button
                className="secondary-button"
                onClick={() => void exportData("json")}
              >
                下載 JSON
              </button>
              <button
                className="secondary-button"
                onClick={() => void exportData("csv")}
              >
                下載 CSV
              </button>
            </div>
          </section>
        </div>
        <aside className="settings-side">
          <section className="card privacy-card">
            <ShieldCheck />
            <h2>資料隱私</h2>
            <p>衡日不使用健康資料投放廣告，也不會把資料提供給其他使用者。</p>
            <a className="privacy-link" href="/privacy.html" target="_blank" rel="noreferrer">查看完整隱私權政策</a>
            <ul>
              <li>
                <Check /> 所有 API 都需登入
              </li>
              <li>
                <Check /> 登出清除本機快取
              </li>
              <li>
                <Check /> 支援完整刪除
              </li>
            </ul>
          </section>
          <button className="logout-card" onClick={onLogout}>
            <LogOut />
            <span>
              <strong>登出此裝置</strong>
              <small>同步後安全清除本機資料</small>
            </span>
            <ChevronRight />
          </button>
          <button className="danger-link" onClick={() => setDeleteOpen(true)}>
            <Trash2 /> 刪除帳號與所有資料
          </button>
        </aside>
      </div>
      {deleteOpen && (
        <DeleteAccountModal
          onClose={() => setDeleteOpen(false)}
          onDeleted={onLogout}
        />
      )}
    </div>
  );
}

function MeasurementModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    const measuredAt = new Date().toISOString();
    const body = {
      id: crypto.randomUUID(),
      measuredAt,
      localDate: today(),
      timezone: "Asia/Taipei",
      weightKg: Number(form.get("weight")),
      bodyFatPct: nullableNumber(form.get("bodyFat")),
      waistCm: nullableNumber(form.get("waist")),
      notes: String(form.get("notes") ?? ""),
    };
    try {
      await apiMutation("/api/v1/measurements", "POST", body, {
        queueOffline: true,
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="記錄今天的身體數據"
      subtitle="數字只是今天的狀態，不是評分。"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field label="體重">
            <div className="unit-input">
              <input
                name="weight"
                type="number"
                step="0.1"
                min="30"
                max="350"
                required
                autoFocus
              />
              <span>kg</span>
            </div>
          </Field>
          <Field label="體脂（選填）">
            <div className="unit-input">
              <input name="bodyFat" type="number" step="0.1" min="1" max="75" />
              <span>%</span>
            </div>
          </Field>
          <Field label="腰圍（選填）">
            <div className="unit-input">
              <input name="waist" type="number" step="0.1" min="30" max="250" />
              <span>cm</span>
            </div>
          </Field>
        </div>
        <Field label="備註（選填）">
          <textarea
            name="notes"
            rows={2}
            maxLength={200}
            placeholder="例如：晨起、訓練後…"
          />
        </Field>
        <ModalActions busy={busy} onClose={onClose} label="儲存紀錄" />
      </form>
    </Modal>
  );
}

function FoodEntryModal({
  food,
  date,
  mealType,
  onClose,
  onSaved,
}: {
  food: Food;
  date: string;
  mealType: MealType;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState(food.servingG);
  const [busy, setBusy] = useState(false);
  const macros = foodMacros(food, amount);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await apiMutation(
        "/api/v1/meals",
        "POST",
        {
          id: crypto.randomUUID(),
          localDate: date,
          mealType,
          foodId: food.id,
          foodName: food.name,
          amountG: amount,
          ...macros,
        },
        { queueOffline: true },
      );
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`加入${mealLabels[mealType]}`}
      subtitle={food.name}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="food-modal-summary">
          <span className="food-avatar large">{food.name.slice(0, 1)}</span>
          <div>
            <strong>{Math.round(macros.calories)} kcal</strong>
            <span>
              P {macros.proteinG} · C {macros.carbsG} · F {macros.fatG} g
            </span>
          </div>
        </div>
        <NumberField
          label="份量"
          value={amount}
          unit="g"
          min={1}
          max={3000}
          step={1}
          onChange={setAmount}
        />
        <div className="portion-presets">
          <button type="button" onClick={() => setAmount(food.servingG / 2)}>
            ½ 份
          </button>
          <button type="button" onClick={() => setAmount(food.servingG)}>
            1 份
          </button>
          <button type="button" onClick={() => setAmount(food.servingG * 1.5)}>
            1.5 份
          </button>
        </div>
        <ModalActions busy={busy} onClose={onClose} label="加入餐點" />
      </form>
    </Modal>
  );
}

function CustomFoodModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (food: Food) => void;
}) {
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    const body = {
      id: crypto.randomUUID(),
      name: String(form.get("name")),
      category: String(form.get("category")),
      servingName: String(form.get("servingName")),
      servingG: Number(form.get("servingG")),
      kcal100g: Number(form.get("kcal")),
      protein100g: Number(form.get("protein")),
      carbs100g: Number(form.get("carbs")),
      fat100g: Number(form.get("fat")),
      fiber100g: nullableNumber(form.get("fiber")) ?? 0,
    };
    try {
      const result = await apiMutation<{ food: Food }>(
        "/api/v1/foods",
        "POST",
        body,
      );
      if ("queued" in result) throw new Error("建立食物需要網路");
      onSaved(result.food);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="建立自訂食物"
      subtitle="營養數值請以每 100 克填寫。"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field label="食物名稱">
            <input name="name" required maxLength={80} autoFocus />
          </Field>
          <Field label="分類">
            <select name="category">
              <option>自訂</option>
              <option>全穀雜糧</option>
              <option>豆魚蛋肉</option>
              <option>蔬菜</option>
              <option>水果</option>
              <option>乳品飲品</option>
            </select>
          </Field>
          <Field label="常用份量名稱">
            <input name="servingName" defaultValue="1 份" required />
          </Field>
          <Field label="每份重量">
            <div className="unit-input">
              <input
                name="servingG"
                type="number"
                defaultValue="100"
                min="1"
                required
              />
              <span>g</span>
            </div>
          </Field>
          <NumberInput name="kcal" label="熱量" unit="kcal" />
          <NumberInput name="protein" label="蛋白質" unit="g" />
          <NumberInput name="carbs" label="碳水" unit="g" />
          <NumberInput name="fat" label="脂肪" unit="g" />
          <NumberInput
            name="fiber"
            label="纖維（選填）"
            unit="g"
            required={false}
          />
        </div>
        <ModalActions busy={busy} onClose={onClose} label="建立食物" />
      </form>
    </Modal>
  );
}

function HydrationHistoryModal({
  onClose,
  notify,
  onChanged,
}: {
  onClose: () => void;
  notify: (message: string, tone?: Toast["tone"]) => void;
  onChanged: () => void;
}) {
  const [logs, setLogs] = useState<HydrationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const result = await apiGet<{ hydration: HydrationLog[] }>("/api/v1/hydration?range=30", { cacheKey: "hydration:30" });
      setLogs(result.hydration);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const remove = async (log: HydrationLog) => {
    try {
      await apiMutation(`/api/v1/hydration/${log.id}`, "DELETE", undefined, { queueOffline: true, version: log.version });
      setLogs((items) => items.filter((item) => item.id !== log.id));
      onChanged();
      notify("飲水紀錄已移除");
    } catch (error) {
      notify(error instanceof Error ? error.message : "無法移除飲水紀錄", "error");
    }
  };
  const dailyTotals = Array.from(logs.reduce((map, log) => map.set(log.localDate, (map.get(log.localDate) ?? 0) + log.amountMl), new Map<string, number>()));
  return (
    <Modal title="飲水歷史" subtitle="最近 30 天的每筆飲水與每日合計。" onClose={onClose}>
      {loading ? <PageSkeleton /> : (
        <div className="hydration-history">
          {dailyTotals.map(([date, total]) => (
            <section key={date}>
              <header><strong>{formatChineseDate(date)}</strong><span>{total} ml</span></header>
              {logs.filter((log) => log.localDate === date).map((log) => (
                <div className="hydration-row" key={log.id}>
                  <Droplets /><span>{log.amountMl} ml</span>
                  <small>{new Date(log.createdAt).toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" })}</small>
                  <button className="icon-button danger" aria-label={`刪除 ${log.amountMl} ml 飲水紀錄`} onClick={() => void remove(log)}><Trash2 /></button>
                </div>
              ))}
            </section>
          ))}
          {!logs.length && <EmptyState icon={<Droplets />} title="還沒有飲水紀錄" description="新增飲水後會出現在這裡。" />}
        </div>
      )}
    </Modal>
  );
}

function WorkoutSessionModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [sets, setSets] = useState<WorkoutSet[]>([
    { exerciseName: "深蹲", setNo: 1, reps: 10, weightKg: 0, rpe: 7 },
    { exerciseName: "深蹲", setNo: 2, reps: 10, weightKg: 0, rpe: 7 },
    { exerciseName: "棒式", setNo: 1, durationSeconds: 45, rpe: 7 },
  ]);
  const updateSet = (index: number, patch: Partial<WorkoutSet>) =>
    setSets((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    );
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await apiMutation(
        "/api/v1/workouts",
        "POST",
        {
          id: crypto.randomUUID(),
          localDate: today(),
          name: String(form.get("name")),
          durationMinutes: Number(form.get("duration")),
          notes: String(form.get("notes") ?? ""),
          sets: sets.map((set, index) => ({
            ...set,
            id: crypto.randomUUID(),
            setNo: index + 1,
          })),
        },
        { queueOffline: true },
      );
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="記錄一場訓練"
      subtitle="先寫下完成的內容，之後再慢慢調整。"
      onClose={onClose}
      size="large"
    >
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field label="訓練名稱">
            <input
              name="name"
              defaultValue="全身訓練"
              maxLength={80}
              required
            />
          </Field>
          <Field label="訓練時間">
            <div className="unit-input">
              <input
                name="duration"
                type="number"
                defaultValue="45"
                min="1"
                max="600"
                required
              />
              <span>分鐘</span>
            </div>
          </Field>
        </div>
        <div className="set-editor">
          <div className="set-head">
            <span>動作</span>
            <span>次數</span>
            <span>重量</span>
            <span>RPE</span>
            <span />
          </div>
          {sets.map((set, index) => (
            <div className="set-row" key={`${set.exerciseName}-${index}`}>
              <input
                value={set.exerciseName}
                onChange={(event) =>
                  updateSet(index, { exerciseName: event.target.value })
                }
                aria-label="動作"
              />
              <input
                type="number"
                value={set.reps ?? ""}
                onChange={(event) =>
                  updateSet(index, { reps: nullableNumber(event.target.value) })
                }
                aria-label="次數"
              />
              <input
                type="number"
                step="0.5"
                value={set.weightKg ?? ""}
                onChange={(event) =>
                  updateSet(index, {
                    weightKg: nullableNumber(event.target.value),
                  })
                }
                aria-label="重量"
              />
              <input
                type="number"
                step="0.5"
                min="1"
                max="10"
                value={set.rpe ?? ""}
                onChange={(event) =>
                  updateSet(index, { rpe: nullableNumber(event.target.value) })
                }
                aria-label="RPE"
              />
              <button
                type="button"
                onClick={() =>
                  setSets((items) =>
                    items.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
                aria-label="刪除此組"
              >
                <X />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="add-set"
            onClick={() =>
              setSets((items) => [
                ...items,
                {
                  exerciseName: "新動作",
                  setNo: items.length + 1,
                  reps: 10,
                  weightKg: 0,
                  rpe: 7,
                },
              ])
            }
          >
            <Plus /> 新增一組
          </button>
        </div>
        <Field label="備註（選填）">
          <textarea name="notes" rows={2} />
        </Field>
        <ModalActions busy={busy} onClose={onClose} label="完成訓練" />
      </form>
    </Modal>
  );
}

function WorkoutTemplateModal({
  template,
  onClose,
  onSaved,
}: {
  template: WorkoutTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<WorkoutTemplate["items"]>(() => template?.items.map((item) => ({ ...item })) ?? [
    { id: crypto.randomUUID(), exerciseName: "深蹲", position: 1, targetSets: 3, targetReps: "8-12", restSeconds: 90 },
    { id: crypto.randomUUID(), exerciseName: "臥推", position: 2, targetSets: 3, targetReps: "8-12", restSeconds: 90 },
    { id: crypto.randomUUID(), exerciseName: "划船", position: 3, targetSets: 3, targetReps: "8-12", restSeconds: 90 },
  ]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const payload = {
        id: template?.id ?? crypto.randomUUID(),
        name: String(form.get("name")),
        notes: String(form.get("notes") ?? ""),
        items: items.map((item, index) => ({ ...item, id: item.id ?? crypto.randomUUID(), position: index + 1 })),
      };
      await apiMutation(
        template ? `/api/v1/workout-templates/${template.id}` : "/api/v1/workout-templates",
        template ? "PUT" : "POST",
        payload,
        { queueOffline: true, version: template?.version },
      );
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={template ? "編輯訓練模板" : "建立訓練模板"}
      subtitle="調整動作、組數、次數與休息時間。"
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <Field label="模板名稱">
          <input
            name="name"
            defaultValue={template?.name ?? "全身訓練 A"}
            required
            maxLength={80}
            autoFocus
          />
        </Field>
        <Field label="備註">
          <textarea
            name="notes"
            rows={2}
            defaultValue={template?.notes ?? "深蹲、推、拉的基礎組合"}
          />
        </Field>
        <div className="template-editor">
          {items.map((item, index) => (
            <div className="template-edit-row" key={item.id ?? index}>
              <input aria-label={`動作 ${index + 1}`} value={item.exerciseName} maxLength={80} onChange={(event) => setItems((current) => current.map((value, itemIndex) => itemIndex === index ? { ...value, exerciseName: event.target.value } : value))} />
              <input aria-label={`${item.exerciseName} 組數`} type="number" min={1} max={20} value={item.targetSets} onChange={(event) => setItems((current) => current.map((value, itemIndex) => itemIndex === index ? { ...value, targetSets: Number(event.target.value) } : value))} />
              <input aria-label={`${item.exerciseName} 次數`} value={item.targetReps} maxLength={30} onChange={(event) => setItems((current) => current.map((value, itemIndex) => itemIndex === index ? { ...value, targetReps: event.target.value } : value))} />
              <input aria-label={`${item.exerciseName} 休息秒數`} type="number" min={0} max={3600} value={item.restSeconds} onChange={(event) => setItems((current) => current.map((value, itemIndex) => itemIndex === index ? { ...value, restSeconds: Number(event.target.value) } : value))} />
              <button type="button" className="icon-button danger" aria-label={`移除 ${item.exerciseName}`} disabled={items.length === 1} onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 /></button>
            </div>
          ))}
          <button type="button" className="secondary-button small" onClick={() => setItems((current) => [...current, { id: crypto.randomUUID(), exerciseName: "新動作", position: current.length + 1, targetSets: 3, targetReps: "8-12", restSeconds: 90 }])}><Plus /> 新增動作</button>
        </div>
        <ModalActions busy={busy} onClose={onClose} label={template ? "儲存變更" : "建立模板"} />
      </form>
    </Modal>
  );
}

function DeleteAccountModal({
  onClose,
  onDeleted,
}: {
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await apiMutation("/api/v1/account", "DELETE", { confirmation: confirm });
      await clearOfflineData();
      onDeleted();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="永久刪除帳號？"
      subtitle="這會移除所有體重、飲食、訓練與同步資料，無法復原。"
      onClose={onClose}
    >
      <div className="danger-box">
        <Trash2 />
        <p>請先匯出需要保留的資料，再輸入「刪除我的資料」。</p>
      </div>
      <Field label="確認文字">
        <input
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoFocus
        />
      </Field>
      <div className="modal-actions">
        <button className="secondary-button" onClick={onClose}>
          取消
        </button>
        <button
          className="danger-button"
          onClick={() => void remove()}
          disabled={confirm !== "刪除我的資料" || busy}
        >
          {busy ? <RefreshCw className="spin" /> : <Trash2 />} 永久刪除
        </button>
      </div>
    </Modal>
  );
}

function MealGroup({
  type,
  entries,
  onAdd,
  onRemove,
}: {
  type: MealType;
  entries: MealEntry[];
  onAdd: () => void;
  onRemove: (entry: MealEntry) => void;
}) {
  const calories = entries.reduce((sum, entry) => sum + entry.calories, 0);
  return (
    <article className="meal-group card">
      <header>
        <div>
          <span className={`meal-dot ${type}`} />
          <h3>{mealLabels[type]}</h3>
          <span>{Math.round(calories)} kcal</span>
        </div>
        <button className="icon-button small" onClick={onAdd}>
          <Plus />
        </button>
      </header>
      <div>
        {entries.map((entry) => (
          <div className="meal-row" key={entry.id}>
            <span className="food-avatar small">
              {entry.foodName.slice(0, 1)}
            </span>
            <div>
              <strong>{entry.foodName}</strong>
              <small>
                {entry.amountG} g · P {Math.round(entry.proteinG)} · C{" "}
                {Math.round(entry.carbsG)} · F {Math.round(entry.fatG)}
              </small>
            </div>
            <span>{Math.round(entry.calories)} kcal</span>
            <button
              onClick={() => onRemove(entry)}
              aria-label={`移除${entry.foodName}`}
            >
              <X />
            </button>
          </div>
        ))}
        {!entries.length && (
          <button className="empty-meal" onClick={onAdd}>
            <Plus /> 新增{mealLabels[type]}
          </button>
        )}
      </div>
    </article>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {hint && <small>{hint}</small>}
      </span>
      {children}
    </label>
  );
}
function NumberField({
  label,
  value,
  unit,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  unit: string;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <Field label={label}>
      <div className="unit-input">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span>{unit}</span>
      </div>
    </Field>
  );
}
function NumberInput({
  name,
  label,
  unit,
  required = true,
}: {
  name: string;
  label: string;
  unit: string;
  required?: boolean;
}) {
  return (
    <Field label={label}>
      <div className="unit-input">
        <input
          name={name}
          type="number"
          min="0"
          step="0.1"
          required={required}
        />
        <span>{unit}</span>
      </div>
    </Field>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
  size = "normal",
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  size?: "normal" | "large";
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`modal ${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header>
          <div>
            <h2 id="modal-title">{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="關閉">
            <X />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}
function ModalActions({
  busy,
  onClose,
  label,
}: {
  busy: boolean;
  onClose: () => void;
  label: string;
}) {
  return (
    <div className="modal-actions">
      <button type="button" className="secondary-button" onClick={onClose}>
        取消
      </button>
      <button className="primary-button" disabled={busy}>
        {busy ? <RefreshCw className="spin" /> : <Check />} {label}
      </button>
    </div>
  );
}

function DateStepper({
  date,
  previous,
  next,
}: {
  date: string;
  previous: () => void;
  next: () => void;
}) {
  return (
    <div className="date-stepper">
      <button onClick={previous} aria-label="前一天">
        <ChevronLeft />
      </button>
      <span>
        <CalendarDays /> {date === today() ? "今天" : formatChineseDate(date)}
      </span>
      <button onClick={next} disabled={date >= today()} aria-label="後一天">
        <ChevronRight />
      </button>
    </div>
  );
}
function MetricMini({
  icon,
  label,
  value,
  unit,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  unit: string;
}) {
  return (
    <div className="metric-mini">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <em>{unit}</em>
      </div>
    </div>
  );
}
function Macro({
  label,
  value,
  target,
  color,
}: {
  label: string;
  value: number;
  target: number;
  color: string;
}) {
  return (
    <div className="macro">
      <div>
        <span>{label}</span>
        <strong>
          {Math.round(value)}
          <small> / {target} g</small>
        </strong>
      </div>
      <div className="progress-line">
        <i
          style={{
            width: `${Math.min(100, percent(value, target))}%`,
            background: color,
          }}
        />
      </div>
    </div>
  );
}
function MetricCard({
  icon,
  tone,
  label,
  value,
  unit,
  progress,
  detail,
  onClick,
}: {
  icon: ReactNode;
  tone: string;
  label: string;
  value: string;
  unit: string;
  progress: number;
  detail: string;
  onClick?: () => void;
}) {
  return (
    <article
      className={`card metric-card ${onClick ? "clickable" : ""}`}
      onClick={onClick}
    >
      <div className={`metric-icon ${tone}`}>{icon}</div>
      <div className="metric-title">
        <span>{label}</span>
        <ChevronRight />
      </div>
      <div className="metric-value">
        <strong>{value}</strong>
        <span>{unit}</span>
      </div>
      <div className="progress-line">
        <i style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
      <small>{detail}</small>
    </article>
  );
}

function ProgressRing({
  value,
  compact = false,
}: {
  value: number;
  compact?: boolean;
}) {
  const radius = compact ? 42 : 58;
  const size = compact ? 104 : 144;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className={`progress-ring ${compact ? "compact" : ""}`}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle className="ring-track" cx={size / 2} cy={size / 2} r={radius} />
        <circle
          className="ring-value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - Math.min(100, value) / 100)}
        />
      </svg>
      {!compact && (
        <div>
          <strong>{value}</strong>
          <span>%</span>
          <small>今日完成</small>
        </div>
      )}
    </div>
  );
}

function WeightChart({ points }: { points: DashboardData["weightTrend"] }) {
  const list = points.slice(-10);
  if (list.length < 2)
    return (
      <div className="empty-chart">
        <TrendingDown />
        <span>再記錄一次就能看趨勢</span>
      </div>
    );
  const values = list.map((point) => point.average);
  const min = Math.min(...values) - 0.5;
  const max = Math.max(...values) + 0.5;
  const coords = values
    .map(
      (value, index) =>
        `${(index / (values.length - 1)) * 100},${50 - ((value - min) / (max - min)) * 44 + 3}`,
    )
    .join(" ");
  return (
    <svg
      className="weight-chart"
      viewBox="0 0 100 56"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="weight-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#477966" stopOpacity=".3" />
          <stop offset="1" stopColor="#477966" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,56 ${coords} 100,56`} fill="url(#weight-fill)" />
      <polyline
        points={coords}
        fill="none"
        stroke="#2f6958"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
function LargeWeightChart({ measurements }: { measurements: Measurement[] }) {
  const points = measurements
    .filter(
      (item): item is Measurement & { weightKg: number } =>
        typeof item.weightKg === "number",
    )
    .sort((a, b) => a.localDate.localeCompare(b.localDate));
  if (points.length < 2)
    return (
      <div className="large-empty-chart">
        <TrendingDown />
        <h3>需要至少兩筆體重紀錄</h3>
        <p>持續記錄後會顯示 7 日平均趨勢。</p>
      </div>
    );
  const values = points.map((item) => item.weightKg);
  const min = Math.min(...values) - 1;
  const max = Math.max(...values) + 1;
  const coords = values
    .map(
      (value, index) =>
        `${4 + (index / (values.length - 1)) * 92},${90 - ((value - min) / (max - min)) * 74}`,
    )
    .join(" ");
  return (
    <svg
      className="large-weight-chart"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <line x1="4" x2="96" y1="25" y2="25" />
      <line x1="4" x2="96" y1="55" y2="55" />
      <line x1="4" x2="96" y1="85" y2="85" />
      <polyline
        points={coords}
        fill="none"
        stroke="#2f6958"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function BarMiniChart({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <div className="bar-chart">
      {values.map((value, index) => (
        <i
          key={index}
          style={{ height: `${Math.max(8, (value / max) * 100)}%` }}
        >
          <span>{value}</span>
        </i>
      ))}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="page skeleton-page">
      <div className="skeleton heading" />
      <div className="skeleton-grid">
        <div className="skeleton block" />
        <div className="skeleton block" />
      </div>
      <div className="skeleton-grid four">
        <div className="skeleton small-block" />
        <div className="skeleton small-block" />
        <div className="skeleton small-block" />
        <div className="skeleton small-block" />
      </div>
    </div>
  );
}
function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

function shiftDate(date: string, days: number): string {
  const result = new Date(`${date}T12:00:00`);
  result.setDate(result.getDate() + days);
  return localDate(result);
}
function daysBetween(a: string, b: string): number {
  return Math.round(
    Math.abs(
      new Date(`${a}T12:00:00`).getTime() - new Date(`${b}T12:00:00`).getTime(),
    ) / 86_400_000,
  );
}
function nullableNumber(
  value: FormDataEntryValue | string | null,
): number | null {
  if (value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
function formatChineseDate(date: string): string {
  return new Intl.DateTimeFormat("zh-TW", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(`${date}T12:00:00`));
}

export default App;
